import { Button, Modal } from 'react-bootstrap';
import { IFwtSummary } from '@nkr/models/index';
import { BreakdownWrapper } from '../styles';

interface IProps {
    show: boolean,
    summaryData: IFwtSummary,
    showLink?: boolean,
    onOpenEWTModal?: (recipientId: number) => void,
    onClose: () => void
}

const FwtDetailsModal = (props: IProps) => {
    const { summaryData } = props;

    if (summaryData == null)
        return null;

    const sectionChecks = {
        Compatible: summaryData.categories.some(c => c.section == 'Compatible'),
        Special: summaryData.categories.some(c => c.section == 'Special'),
        Regular: summaryData.categories.some(c => c.section == 'Regular'),
        General: summaryData.categories.some(c => c.section == 'General'),
    };

    let rowLabelIndex = 1;
    let isHardToMatch = summaryData.hardToMatch;
    let isPairedFavorable = summaryData.isPairedRecipient && ['A', 'B', 'AB'].includes(summaryData.recipientBloodType) && summaryData.aboCombo == 'Favourable';
    let isPairedUnfavorable = summaryData.isPairedRecipient && ['O'].includes(summaryData.recipientBloodType) && summaryData.aboCombo == 'Unfavourable'
    let hideTable = isHardToMatch || isPairedFavorable || isPairedUnfavorable;

    // Infinity guard function
    const safeValue = (val) => val == 'N/A' ? 'N/A' : (isFinite(val) ? val : 'N/A');

    let cpra4 = safeValue(props.summaryData.patientCpra.toFixed(0));
    let perAvoidComp = safeValue(100 - Number(props.summaryData.patientCpra));
    let donorDeclineRatio = safeValue(props.summaryData.patientHistoricalDeclineRatio.toFixed(0));
    let donorAccRate = safeValue(100 - Number(props.summaryData.patientHistoricalDeclineRatio.toFixed(0)));
    let nkrCprandDonorAccRate = safeValue(perAvoidComp * donorAccRate / 100);
    let reqDonorEncounters = safeValue(Math.ceil(1 / (nkrCprandDonorAccRate / 100)));
    let foreCastedCompatibleDonors = safeValue(props.summaryData.aboAndAgeCompatibleDonors);
    let foreCastedWaitTimeDecimal = safeValue((reqDonorEncounters / Number(props.summaryData.aboAndAgeCompatibleDonors)).toFixed(2));
    let acceptiableCompDonors = safeValue((nkrCprandDonorAccRate * foreCastedCompatibleDonors / 100).toFixed(2));
    let baselineWaitTime = safeValue(acceptiableCompDonors < 1 ? 60 : acceptiableCompDonors >= 1 && acceptiableCompDonors < 15 ? 8 : 4);
    let actualWaitTime = Number(props.summaryData.awt);
    let foreCastedWaitTimeX = safeValue(Math.ceil(foreCastedWaitTimeDecimal)) == 'N/A' ? '60+' : safeValue(Math.ceil(foreCastedWaitTimeDecimal));
    let foreCastedWaitTimeY = baselineWaitTime == 60 ? 60 : actualWaitTime >= baselineWaitTime ? 3 : safeValue(baselineWaitTime - actualWaitTime);

    let isX = (summaryData.isVoucherHolder && ['O'].includes(summaryData.recipientBloodType) && sectionChecks.Compatible)
        || (summaryData.isVoucherHolder && !['O'].includes(summaryData.recipientBloodType))
        || (summaryData.isPairedRecipient && ['O'].includes(summaryData.recipientBloodType) && summaryData.aboCombo == 'Neutral')
        || (summaryData.isPairedRecipient && !['O'].includes(summaryData.recipientBloodType) && summaryData.aboCombo == 'Neutral')

    if (isX == true)
        hideTable = hideTable || donorAccRate == 0 || perAvoidComp == 0;

    let isY = (summaryData.isVoucherHolder && ['O'].includes(summaryData.recipientBloodType) && (sectionChecks.Special || sectionChecks.Regular))    
    let fwtCalculation = (baselineWaitTime == "60") ? "Equals Row 8 if 60+" : actualWaitTime >= baselineWaitTime ? "Row 8 - Row 9 with a minimum of 3 months" : "Row 8 - Row 9";
    let getNextRowLabel = () => `${rowLabelIndex++}`;

    let showTop = isHardToMatch || (donorAccRate == 0 && isHardToMatch == false) || (perAvoidComp == 0 && isHardToMatch == false) || isPairedFavorable || isPairedUnfavorable;

    return (
        <>
            <Modal backdrop='static' bsSize='large' show={props.show} onHide={props.onClose}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        Forecasted Wait Time Detail (<span><b>Alias:</b> {props.summaryData.recipientAlias}, <b>ABO:</b> {props.summaryData.recipientBloodType}, <b>Age:</b> {props.summaryData.age}</span>)
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className='row'>
                        <div className='col-xs-12' style={{ padding: '0 7.5px' }}>
                            <ul style={{ fontSize: '100%' }}>
                                {isHardToMatch &&
                                    <li>
                                        This patient’s Forecasted Wait Time equals the{' '}
                                        {props.showLink ?
                                            <a
                                                href='#'
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    props.onOpenEWTModal(props.summaryData.recipientId);
                                                }}
                                            >
                                                Estimated Wait Time
                                            </a>
                                            :
                                            <>Estimated Wait Time</>
                                        }{' '}
                                        due to their status as Hard to Match.
                                    </li>
                                }

                                {donorAccRate == 0 && isHardToMatch == false &&
                                    <li>
                                        This patient's forecasted wait time is  &gt; 60 months due to 100% Donor Decline Ratio Historic (DDRH).
                                    </li>
                                }

                                {perAvoidComp == 0 && isHardToMatch == false &&
                                    <li>
                                        This patient's forecasted wait time is  &gt; 60 months due to 100% NKR cPRA.
                                    </li>
                                }

                                {isPairedFavorable &&
                                    <li>
                                        This patient's forecasted wait time is {summaryData.fwt} months because they have a blood type combination that is typically quick to match.
                                    </li>
                                }

                                {isPairedUnfavorable &&
                                    <li>
                                        This patient’s forecasted wait time is <strong>&gt; 60 months</strong> due to an unfavorable blood type combination.
                                    </li>
                                }

                                {isPairedUnfavorable &&
                                    <li>
                                        We recommend enrolling the pair into the voucher program.
                                    </li>
                                }
                            </ul>
                        </div>
                    </div>

                    {!hideTable && (
                        <div style={{ paddingTop: showTop ? 20 : 0 }}>
                            <BreakdownWrapper condensed bordered>
                                <thead>
                                    <tr>
                                        <th>Row #</th>
                                        <th>Summary</th>
                                        <th>Calculation</th>
                                        <th>Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(isX || isY) && (
                                        <>
                                            <tr>
                                                <td className='rowLabel' style={{ textAlign: 'center' }}>{getNextRowLabel()}</td>
                                                <td>NKR cPRA as Percent</td>
                                                <td>Number of donors avoid incompatible with patient / Total number of donors</td>
                                                <td>{cpra4 + '%'}</td>
                                            </tr>
                                            <tr>
                                                <td className='rowLabel' style={{ textAlign: 'center' }}>{getNextRowLabel()}</td>
                                                <td>Percent avoid compatible donors with patient</td>
                                                <td>100% – Row 1</td>
                                                <td>{perAvoidComp.toFixed(0) + '%'}</td>
                                            </tr>
                                            <tr>
                                                <td className='rowLabel' style={{ textAlign: 'center' }}>{getNextRowLabel()}</td>
                                                <td>Donor Decline Ratio Historic (DDRH)</td>
                                                <td>Pre-Select Declines / Potential Donors in Pre-Selects</td>
                                                <td>{donorDeclineRatio + '%'}</td>
                                            </tr>
                                            <tr>
                                                <td className='rowLabel' style={{ textAlign: 'center' }}>{getNextRowLabel()}</td>
                                                <td>Donor Acceptance Rate</td>
                                                <td>100% – Row 3</td>
                                                <td>{donorAccRate.toFixed(0) + '%'}</td>
                                            </tr>

                                            <tr>
                                                <td className='rowLabel' style={{ textAlign: 'center' }}>{getNextRowLabel()}</td>
                                                <td>NKR cPRA and Donor Acceptance Rate</td>
                                                <td>Row 2 x Row 4</td>
                                                <td>{nkrCprandDonorAccRate.toFixed(0) + '%'}</td>
                                            </tr>
                                        </>
                                    )}

                                    {isX &&                         
                                            <>
                                                <tr>
                                                    <td className='rowLabel' style={{ textAlign: 'center' }}>{getNextRowLabel()}</td>
                                                    <td>Required donor encounters to yield 1 match</td>
                                                    <td>1 / Row 5</td>
                                                    <td>{reqDonorEncounters}</td>
                                                </tr>
                                            </>
                                    }

                                    {(isX || isY) &&
                                        <>
                                            <tr>
                                                <td className='rowLabel' style={{ textAlign: 'center' }}>{getNextRowLabel()}</td>
                                                <td>Forecasted Compatible Donors (per month)</td>
                                                <td>ABO and Age Compatible Donors<br />(12 month moving average)</td>
                                                <td>{foreCastedCompatibleDonors}</td>
                                            </tr>
                                        </>
                                    }

                                    {isX &&
                                        <>
                                            <tr>
                                                <td className='rowLabel' style={{ textAlign: 'center' }}>{getNextRowLabel()}</td>
                                                <td>Forecasted Wait Time as decimal (fractional months)</td>
                                                <td>Row 6 / Row 7</td>
                                                <td>{foreCastedWaitTimeDecimal}</td>
                                            </tr>

                                            <tr>
                                                <td className='rowLabel' style={{ textAlign: 'center' }}>{getNextRowLabel()}</td>
                                                <td><b>Forecasted Wait Time (months)</b></td>
                                                <td><b>Round up Row 8</b></td>
                                                <td style={{ backgroundColor: 'yellow' }}><b>{foreCastedWaitTimeX}</b></td>
                                            </tr>
                                        </>
                                    }

                                    {isY &&
                                        <>
                                            <tr>
                                                <td className='rowLabel' style={{ textAlign: 'center' }}>{getNextRowLabel()}</td>
                                                <td>Acceptable Compatible Donors (per month)<sup><b>1</b></sup></td>
                                                <td>Row 5 x Row 6</td>
                                                <td>{acceptiableCompDonors}</td>
                                            </tr>

                                             <tr>
                                                <td className='rowLabel' style={{ textAlign: 'center' }}>{getNextRowLabel()}</td>
                                                <td>Baseline Wait Time (months)<sup><b>2</b></sup></td>
                                                <td>Based on Row 7, using lookup table below</td>
                                                <td>{baselineWaitTime == 60 ? baselineWaitTime + '+' : baselineWaitTime}</td>
                                            </tr>
                                                                    
                                            <tr>
                                                <td className='rowLabel' style={{ textAlign: 'center' }}>{getNextRowLabel()}</td>
                                                <td>Actual Wait Time (months)</td>
                                                <td>Patient's actual wait time in NKR</td>
                                                <td>{actualWaitTime}</td>
                                            </tr>

                                            <tr>
                                                <td className='rowLabel' style={{ textAlign: 'center' }}>{getNextRowLabel()}</td>
                                                <td><b>Forecasted Wait Time (months)</b></td>
                                                <td><b>{fwtCalculation}</b></td>
                                                <td style={{ backgroundColor: 'yellow' }}><b>{foreCastedWaitTimeY == 60 ? '60+' : foreCastedWaitTimeY}</b></td>
                                            </tr>
                                        </>
                                    }
                                </tbody>
                            </BreakdownWrapper>

                            {isY &&
                                <BreakdownWrapper condensed bordered style={{ width: '70%' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '5%' }}>Row</th>
                                            <th style={{ width: '50%' }}>Acceptable Compatible Donors (per month)<sup><b>1</b></sup></th>
                                            <th style={{ width: '45%' }}>Baseline Wait Time (months)<sup><b>2</b></sup></th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr>
                                            <td style={{ textAlign: 'center' }}>{'A'}</td><td style={{ textAlign: 'center' }}>{'< 1'}</td><td colSpan={1} style={{ textAlign: 'center' }}>60+</td>
                                        </tr>
                                        <tr>
                                            <td style={{ textAlign: 'center' }}>{'B'}</td><td style={{ textAlign: 'center' }}> {'>=1 & < 15'}</td><td colSpan={1} style={{ textAlign: 'center' }}>8</td>
                                        </tr>
                                        <tr>
                                            <td style={{ textAlign: 'center' }}>{'C'}</td><td style={{ textAlign: 'center' }}>{'>= 15'}</td><td colSpan={1} style={{ textAlign: 'center' }}>4</td>
                                        </tr>
                                    </tbody>

                                </BreakdownWrapper>
                            }
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={props.onClose}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default FwtDetailsModal;